import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import * as R from 'ramda';
import { Product } from '@inter-ikea-storageone/range';
import { ProductDesign } from '@inter-ikea-storageone/range';
import {
  getSelectedGroupId,
  getFilterState,
  filterApplied,
  getProductsForGroup
} from 'ducks/products';
import ColorPicker from '../../../../components/ProductSidebar/menus/Widgets/ChangeColor/ColorPicker';
import { usePlannerConfig } from 'contexts/plannerConfig';
import { useAppDispatch } from 'ducks/hooks';
import { useCore } from 'contexts/core';
import { isMobile } from 'utils/platform';
import Title from 'ui/Typography/Title';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import { useIntl } from 'react-intl';

interface ColorCounts {
  [color: string]: number;
}

const getProductDesigns = (products: Product[]) => {
  const designs = products.map(product => product.design);
  return R.uniq(designs);
};

const getMostFrequentColor = (colorCounts: ColorCounts) =>
  Object.keys(colorCounts).reduce(
    (leadingColor, color) =>
      colorCounts[color] > colorCounts[leadingColor] ? color : leadingColor,
    Object.keys(colorCounts)[0]
  ) as ProductDesign;

const withoutAlternativeVariants = (products: Product[]) =>
  products.reduce(
    (includedProducts, product) =>
      includedProducts.find(
        alreadyIncludedProduct => alreadyIncludedProduct.globalItemNo === product.globalItemNo
      )
        ? includedProducts
        : [...includedProducts, product],
    [] as Product[]
  );

const ChangeColorFilter = () => {
  const coreApi = useCore();
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const filterState = useSelector(getFilterState);
  const selectedGroupId = useSelector(getSelectedGroupId);
  const productsInGroup = useSelector(getProductsForGroup);
  const {
    plannerConfig: { productGroups }
  } = usePlannerConfig();

  const selectedGroup = R.find(R.propEq('id', selectedGroupId), productGroups);
  const designsInGroup = getProductDesigns(productsInGroup).filter(Boolean) as ProductDesign[];

  const getDefaultColor = () => {
    const entitiesOnScene = coreApi.entities.getAll();
    const idsOnScene = entitiesOnScene.map(entity => coreApi.entities.getProductRef(entity));
    const productsInGroupOnScene = idsOnScene
      .map(id => productsInGroup.find(p => p.coreProductId === id))
      .filter(p => !!p);

    const colorsOfProductsInGroupOnScene = productsInGroupOnScene.map(p => p!.design);
    const colorCountsOnScene = colorsOfProductsInGroupOnScene.reduce((result, color) => {
      const updatedResult = { ...result };
      updatedResult[color] = result[color] ? result[color] + 1 : 1;
      return updatedResult;
    }, {} as ColorCounts);

    if (Object.keys(colorCountsOnScene).length) {
      return getMostFrequentColor(colorCountsOnScene);
    } else {
      const productsInGroupWithoutAlternativeVariants = withoutAlternativeVariants(productsInGroup);
      const colorCountsInGroup = designsInGroup.reduce((result, color) => {
        const updatedResult = { ...result };
        updatedResult[color] = productsInGroupWithoutAlternativeVariants.filter(
          product => product.design === color
        ).length;
        return updatedResult;
      }, {} as ColorCounts);
      return getMostFrequentColor(colorCountsInGroup);
    }
  };

  const [currentColor, setCurrentColor] = useState(getDefaultColor());

  const filterProducts = useCallback(
    (design: ProductDesign) => {
      if (selectedGroup) {
        const colorFilterState = filterState(selectedGroupId, 'design');

        if (currentColor !== design) {
          setCurrentColor(design);
        }
        if (colorFilterState.design !== design) {
          const newFilterState = { ...colorFilterState, design };
          const filteredProducts = productsInGroup
            .filter(p => p.design === design)
            .map(p => p.coreProductId);
          dispatch(
            filterApplied({
              id: 'design',
              productGroupId: selectedGroup.id,
              productIdList: filteredProducts,
              filterState: newFilterState
            })
          );
        }
      }
    },
    [currentColor, dispatch, filterState, productsInGroup, selectedGroup, selectedGroupId]
  );

  useEffect(() => {
    const colorFilterState = filterState(selectedGroupId, 'design');
    if (!colorFilterState.design) {
      filterProducts(getDefaultColor());
    } else {
      setCurrentColor(colorFilterState.design);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {!isMobile() && (
        <Title type={ThemeFontStyleTypeEnum.headingSmall} pb="xxs">
          {formatMessage({ id: 'storageone.colorFilterLabel' })}
        </Title>
      )}
      <ColorPicker
        colors={designsInGroup}
        currentColor={currentColor}
        onChangeColor={filterProducts}
      />
    </>
  );
};

export default ChangeColorFilter;
